import { SimulationResult } from '@/types/simulations';
import { convertNumber, formatDate } from '@/utils';

// Function to get translation function - will be passed from parent component
export type TranslationFunction = (key: string) => string;

export interface TableData {
  plants: Array<{
    plant: SimulationResult['plants'][0];
    filteredSimulations: SimulationResult[];
  }>;
  scenarios: Array<{
    scenario: SimulationResult['scenario'];
    filteredSimulations: SimulationResult[];
  }>;
  storages: Array<{
    storage: SimulationResult['storage'];
    filteredSimulations: SimulationResult[];
  }>;
}

export const getUniqueIds = (simulationDetails: SimulationResult[] | null) => {
  if (!simulationDetails) return { plants: [], scenarios: [], storages: [] };

  const plants = new Set(
    simulationDetails
      .filter((sim) => sim?.plants?.[0]?.id)
      .map((sim) => sim.plants[0].id)
  );
  const scenarios = new Set(
    simulationDetails
      .filter((sim) => sim?.scenario?.id)
      .map((sim) => sim.scenario.id)
  );
  const storages = new Set(
    simulationDetails
      .filter((sim) => sim?.storage?.id)
      .map((sim) => sim.storage.id)
  );

  return {
    plants: Array.from(plants),
    scenarios: Array.from(scenarios),
    storages: Array.from(storages),
  };
};

export const getAfterStorageTableColumns = (
  t: TranslationFunction,
  currencySymbol: string = '$'
) => [
  {
    title: t('components.tableUtils.columns.simulationName'),
    dataIndex: 'name',
    key: 'name',
    width: 150,
    ellipsis: true,
    render: (text: string) => {
      if (!text) return '-';
      return text.length > 20 ? `${text.substring(0, 20)}...` : text;
    },
  },
  {
    title: t('components.tableUtils.columns.startDate'),
    dataIndex: 'startDate',
    key: 'startDate',
    width: 140,
    ellipsis: true,
    render: (date: string) => {
      return formatDate(date);
    },
  },
  {
    title: t('components.tableUtils.columns.endDate'),
    dataIndex: 'endDate',
    key: 'endDate',
    width: 140,
    ellipsis: true,
    render: (date: string) => {
      return formatDate(date);
    },
  },
  {
    title: t('components.tableUtils.columns.scenario'),
    dataIndex: ['scenario', 'name'],
    key: 'scenarioName',
    width: 150,
    ellipsis: true,
    render: (text: string) => {
      if (!text) return '-';
      return text.length > 20 ? `${text.substring(0, 20)}...` : text;
    },
  },
  {
    title: t('components.tableUtils.columns.storage'),
    dataIndex: ['storage', 'name'],
    key: 'storageName',
    width: 150,
    ellipsis: true,
    render: (text: string) => {
      if (!text) return '-';
      return text.length > 20 ? `${text.substring(0, 20)}...` : text;
    },
  },
  {
    title: t('components.tableUtils.columns.afterStorage'),
    children: [
      {
        title: t('components.tableUtils.columns.irr'),
        dataIndex: ['result', 'irrValue'],
        key: 'irrValue',
        width: 120,
        align: 'center' as const,
        render: (value: number) => {
          if (value === undefined || value === null) return '-';
          return `${convertNumber(value)}%`;
        },
      },
      {
        title: t('components.tableUtils.columns.ebitda'),
        dataIndex: ['result', 'ebitda'],
        key: 'ebitda',
        width: 140,
        align: 'center' as const,
        render: (value: number) => {
          if (value === undefined || value === null) return '-';
          return `${currencySymbol}${convertNumber(value)}`;
        },
      },
      {
        title: t('components.tableUtils.columns.capex'),
        dataIndex: ['result', 'capexWithStorage'],
        key: 'capexWithStorage',
        width: 120,
        align: 'center' as const,
        render: (value: number) => {
          if (value === undefined || value === null) return '-';
          return `${currencySymbol}${convertNumber(value)}`;
        },
      },
      {
        title: t('components.tableUtils.columns.dpr'),
        dataIndex: ['result', 'discountedPaybackPeriod'],
        key: 'discountedPaybackPeriod',
        width: 120,
        ellipsis: false,
        align: 'center' as const,
        render: (value: number) => {
          if (value === undefined || value === null) return '-';
          return value.toFixed(2);
        },
      },
    ],
  },
  {
    title: t('components.tableUtils.columns.storage'),
    children: [
      {
        title: t('components.tableUtils.columns.irr'),
        dataIndex: ['result', 'irrStorage'],
        key: 'irrStorage',
        width: 120,
        align: 'center' as const,
        render: (value: number) => {
          if (value === undefined || value === null) return '-';
          return `${convertNumber(value)}%`;
        },
      },
      {
        title: t('components.tableUtils.columns.ebitda'),
        dataIndex: ['result', 'ebitdaStorage'],
        key: 'ebitdaStorage',
        width: 120,
        align: 'center' as const,
        render: (value: number) => {
          if (value === undefined || value === null) return '-';
          return `${currencySymbol}${convertNumber(value)}`;
        },
      },
      {
        title: t('components.tableUtils.columns.capex'),
        dataIndex: ['result', 'totalStorageCapex'],
        key: 'totalStorageCapex',
        width: 120,
        align: 'center' as const,
        render: (value: number) => {
          if (value === undefined || value === null) return '-';
          return `${currencySymbol}${convertNumber(value)}`;
        },
      },
      {
        title: t('components.tableUtils.columns.dpr'),
        dataIndex: ['result', 'discountedPaybackPeriodStorage'],
        key: 'discountedPaybackPeriodStorage',
        width: 120,
        align: 'center' as const,
        render: (value: number) => {
          if (value === undefined || value === null) return '-';
          return value.toFixed(2);
        },
      },
    ],
  },
];

export const getBeforeStorageTableColumns = (
  t: TranslationFunction,
  currencySymbol: string = '$'
) => [
  {
    title: t('components.tableUtils.columns.simulationName'),
    dataIndex: 'name',
    key: 'name',
    width: 150,
    ellipsis: true,
    render: (text: string) => {
      if (!text) return '-';
      return text.length > 20 ? `${text.substring(0, 20)}...` : text;
    },
  },
  {
    title: t('components.tableUtils.columns.startDate'),
    dataIndex: 'startDate',
    key: 'startDate',
    width: 150,
    ellipsis: true,
    render: (date: string) => {
      return formatDate(date);
    },
  },
  {
    title: t('components.tableUtils.columns.endDate'),
    dataIndex: 'endDate',
    key: 'endDate',
    width: 150,
    ellipsis: true,
    render: (date: string) => {
      return formatDate(date);
    },
  },
  {
    title: t('components.tableUtils.columns.plants'),
    dataIndex: ['plants', '0', 'name'],
    key: 'plantName',
    width: 150,
    ellipsis: true,
    render: (text: string) => {
      if (!text) return '-';
      return text.length > 20 ? `${text.substring(0, 20)}...` : text;
    },
  },
  {
    title: t('components.tableUtils.columns.scenario'),
    dataIndex: ['scenario', 'name'],
    key: 'scenarioName',
    width: 150,
    ellipsis: true,
    render: (text: string) => {
      if (!text) return '-';
      return text.length > 20 ? `${text.substring(0, 20)}...` : text;
    },
  },
  {
    title: t('components.tableUtils.columns.beforeStorage'),
    children: [
      {
        title: t('components.tableUtils.columns.irr'),
        dataIndex: ['result', 'irrValueWos'],
        key: 'irrValueWos',
        width: 120,
        align: 'center' as const,
        render: (value: number) => {
          if (value === undefined || value === null) return '-';
          return `${convertNumber(value)}%`;
        },
      },
      {
        title: t('components.tableUtils.columns.ebitda'),
        dataIndex: ['result', 'ebitdaWos'],
        key: 'ebitdaWos',
        width: 140,
        align: 'center' as const,
        render: (value: number) => {
          if (value === undefined || value === null) return '-';
          return `${currencySymbol}${convertNumber(value)}`;
        },
      },
      {
        title: t('components.tableUtils.columns.capex'),
        dataIndex: ['result', 'capexWithoutStorage'],
        key: 'capexWithoutStorage',
        width: 120,
        align: 'center' as const,
        render: (value: number) => {
          if (value === undefined || value === null) return '-';
          return `${currencySymbol}${convertNumber(value)}`;
        },
      },
      {
        title: t('components.tableUtils.columns.dpr'),
        dataIndex: ['result', 'discountedPaybackPeriodWos'],
        key: 'discountedPaybackPeriodWos',
        width: 120,
        ellipsis: false,
        align: 'center' as const,
        render: (value: number) => {
          if (value === undefined || value === null) return '-';
          return value.toFixed(2);
        },
      },
    ],
  },
];

export const getMemoizedTableData = (
  simulationDetails: SimulationResult[] | null,
  uniqueIds: ReturnType<typeof getUniqueIds>
): TableData => {
  if (!simulationDetails) return { plants: [], scenarios: [], storages: [] };

  const plants = uniqueIds.plants
    .map((plantId) => {
      const plant = simulationDetails.find(
        (sim) => sim?.plants?.[0]?.id === plantId
      )?.plants[0];
      if (!plant) return null;
      const filteredSimulations = simulationDetails.filter(
        (sim) => sim?.plants?.[0]?.id === plantId
      );
      return { plant, filteredSimulations };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  const scenarios = uniqueIds.scenarios
    .map((scenarioId) => {
      const scenario = simulationDetails.find(
        (sim) => sim?.scenario?.id === scenarioId
      )?.scenario;
      if (!scenario) return null;
      const filteredSimulations = simulationDetails.filter(
        (sim) => sim?.scenario?.id === scenarioId
      );
      return { scenario, filteredSimulations };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  const storages = uniqueIds.storages
    .map((storageId) => {
      const storage = simulationDetails.find(
        (sim) => sim?.storage?.id === storageId
      )?.storage;
      if (!storage) return null;
      const filteredSimulations = simulationDetails.filter(
        (sim) => sim?.storage?.id === storageId
      );
      return { storage, filteredSimulations };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  return { plants, scenarios, storages };
};
